
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Shield, Edit3, Save, X, Info, AlertTriangle } from "lucide-react";
import { useMutation as useConvexMutation, useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { toast } from "sonner";
import { useRestrictionHierarchy } from "@/hooks/useRestrictionHierarchy";
import { RestrictionSetting } from "@/components/hardcodedRestrictions";
import { ErrorBoundary } from "./ErrorBoundary";
import { type GenericId as Id } from "convex/values";

interface LevelRestrictionsTabProps {
  levelId: string;
  restrictions?: any;
}

export const LevelRestrictionsTab = ({ levelId, restrictions }: LevelRestrictionsTabProps) => {
  console.log("LevelRestrictionsTab: Component mounted/rendered for levelId:", levelId);
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRestrictions, setEditedRestrictions] = useState<Record<string, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { flatRestrictions, shouldChildBeDisabled, restrictions: allRestrictions } = useRestrictionHierarchy();

  // Fetch level data using Convex to get current restrictions
  const levelData = useConvexQuery(api.levels.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100
    }
  });

  const currentLevel = levelData?.page?.find(level => level.name === levelId);
  const isLoading = levelData === undefined;

  // Parse restrictions from the level's restrictions array
  const levelRestrictions = currentLevel?.restrictions || [];
  
  // Convert restrictions array to restriction data object
  const restrictionData: Record<string, boolean> = {};
  
  // Initialize all restrictions as allowed (true) by default
  allRestrictions.forEach(restriction => {
    restrictionData[restriction.type] = true;
  });
  
  // Mark restrictions that exist in the level's restrictions array as restricted (false)
  levelRestrictions.forEach(restrictionKey => {
    // Find the restriction by backend_name
    const restriction = allRestrictions.find(r => r.backend_name === restrictionKey);
    if (restriction) {
      restrictionData[restriction.type] = false;
    }
  });

  const updateLevelRestrictions = useConvexMutation(api.levels.restrictions.update);

  const getCurrentRestrictionValues = (): Record<string, boolean> => {
    const values: Record<string, boolean> = {};
    
    allRestrictions.forEach(setting => {
      if (isEditMode && setting.type in editedRestrictions) {
        values[setting.type] = editedRestrictions[setting.type];
      } else {
        values[setting.type] = restrictionData[setting.type] ?? true;
      }
    });
    
    return values;
  };

  const getRestrictionValue = (restrictionType: string): boolean => {
    const currentValues = getCurrentRestrictionValues();
    return currentValues[restrictionType] ?? true;
  };

  const saveRestrictionsMutation = {
    mutate: async (restrictionValues: Record<string, boolean>) => {
      try {
        // Convert restriction values to array format expected by Convex
        const restrictionsArray: string[] = [];
        Object.entries(restrictionValues).forEach(([restrictionType, value]) => {
          if (!value) { // If restricted (false), add the backend name to restrictions array
            const restriction = allRestrictions.find(r => r.type === restrictionType);
            if (restriction) {
              restrictionsArray.push(restriction.backend_name);
            }
          }
        });

        await updateLevelRestrictions({
          level: levelId as Id<"levels">,
          restrictions: restrictionsArray
        });
        
        setIsEditMode(false);
        setEditedRestrictions({});
        setValidationErrors([]);
        toast.success("Level restrictions saved successfully");
      } catch (error) {
        console.error('Error saving level restrictions:', error);
        
        // Handle Convex validation errors specifically
        if (error instanceof Error && error.message.includes("doesn't exist")) {
          const errorMatch = error.message.match(/Restriction '([^']+)' doesn't exist/);
          const invalidRestriction = errorMatch ? errorMatch[1] : 'unknown';
          
          setValidationErrors(prev => [...prev, invalidRestriction]);
          toast.error(`Restriction '${invalidRestriction}' is not supported by the backend. Please contact your administrator.`);
        } else {
          toast.error("Failed to save level restrictions");
        }
        throw error;
      }
    },
    isPending: false
  };

  const handleEditRestriction = (restrictionType: string, value: boolean) => {
    // Clear any validation errors for this restriction
    setValidationErrors(prev => prev.filter(error => error !== restrictionType));
    
    const currentValues = getCurrentRestrictionValues();
    const newEditedValues = { ...editedRestrictions, [restrictionType]: value };
    
    // Handle parent-child relationships
    const restriction = allRestrictions.find(r => r.type === restrictionType);
    
    if (restriction) {
      // If this is a parent and being restricted, restrict all children
      if (!restriction.parent_id && !value) {
        const children = allRestrictions.filter(r => r.parent_id === restriction.type);
        children.forEach(child => {
          newEditedValues[child.type] = false;
        });
      }
      
      // If this is a child and being allowed, but parent is restricted, prevent it
      if (restriction.parent_id && value) {
        const parent = allRestrictions.find(r => r.type === restriction.parent_id);
        if (parent) {
          const parentValue = newEditedValues[parent.type] !== undefined 
            ? newEditedValues[parent.type] 
            : currentValues[parent.type];
          
          if (!parentValue) {
            toast.error(`Cannot allow ${restriction.name} while ${parent.name} is restricted`);
            return;
          }
        }
      }
    }
    
    setEditedRestrictions(newEditedValues);
  };

  const handleSave = () => {
    const finalValues = getCurrentRestrictionValues();
    saveRestrictionsMutation.mutate(finalValues);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedRestrictions({});
    setValidationErrors([]);
  };

  if (isLoading) {
    return (
      <div className="h-full w-full flex flex-col">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Device Restrictions</h3>
          <p className="text-muted-foreground">Loading device restrictions...</p>
        </div>
      </div>
    );
  }

  if (!currentLevel) {
    return (
      <div className="h-full w-full flex flex-col">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Device Restrictions</h3>
          <p className="text-red-500">Level not found. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <TooltipProvider>
        <div className="h-full w-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h3 className="text-lg font-semibold">Device Restrictions</h3>
              <p className="text-muted-foreground">
                Configure device feature restrictions for this level
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {!isEditMode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditMode(true)}
                  className="flex items-center space-x-2"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit</span>
                </Button>
              ) : (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    className="flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saveRestrictionsMutation.isPending}
                    className="flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save</span>
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Content area with proper padding */}
          <div className="flex-1 overflow-auto p-6">
            {/* Validation Errors Warning */}
            {validationErrors.length > 0 && (
              <Card className="border-orange-200 bg-orange-50 mb-6">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-orange-900">Unsupported Restrictions</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        The following restrictions are not supported by the backend: {validationErrors.join(', ')}
                      </p>
                      <p className="text-xs text-orange-600 mt-1">
                        Please contact your administrator to add support for these restrictions.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Restrictions Table */}
            <ErrorBoundary>
              <Card className="mb-6">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Restriction</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flatRestrictions.map((setting) => {
                        const value = getRestrictionValue(setting.type);
                        const isDisabled = !isEditMode;
                        const isChild = Boolean(setting.parent_id);
                        const currentValues = getCurrentRestrictionValues();
                        const childDisabled = shouldChildBeDisabled(setting, currentValues);
                        
                        return (
                          <TableRow key={setting.type} className={isChild ? "bg-muted/30" : ""}>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <span className={`font-medium ${isChild ? 'ml-6 text-muted-foreground' : ''}`}>
                                  {isChild && '└─ '}{setting.name}
                                </span>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Backend key: {setting.backend_name}</p>
                                    {isChild && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Child restriction - automatically restricted when parent is restricted
                                      </p>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <Switch
                                  checked={value}
                                  onCheckedChange={(checked) => {
                                    handleEditRestriction(setting.type, checked);
                                  }}
                                  disabled={isDisabled || childDisabled}
                                  className={`
                                    ${value 
                                      ? 'data-[state=checked]:bg-green-600' 
                                      : 'data-[state=unchecked]:bg-red-500'
                                    }
                                  `}
                                />
                                <span className={`text-sm font-medium ${
                                  value ? 'text-green-600' : 'text-red-500'
                                }`}>
                                  {value ? 'Allowed' : 'Restricted'}
                                </span>
                                {childDisabled && (
                                  <Badge variant="secondary" className="text-xs">
                                    Auto-restricted
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </ErrorBoundary>

            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Device Restrictions</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Configure restrictions here to automatically apply them to all devices assigned to this level. 
                      Changes will be synchronized to devices during their next check-in.
                      Parent restrictions automatically restrict their child restrictions when disabled.
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      Note: Only restrictions supported by your backend configuration will be saved successfully.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </TooltipProvider>
    </ErrorBoundary>
  );
};
